import { Children, forwardRef, isValidElement, type ElementType, type ReactNode } from 'react';

import { stackRecipe } from './Stack.css';
import { StackItem } from './StackItem';
import { getClassNames } from '../../util/css';
import { type BooleanVariant, type NumberVariant } from '../../util/css/vanilla-extract';
import { type RecipeVariants } from '../../vanilla-extract.css';

const sparkyClassName = 'sparky-stack';

type GapValue = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 10 | 12 | 16;

type StackRecipeVariants = RecipeVariants<typeof stackRecipe>;

type StackProps = Omit<StackRecipeVariants, 'gap' | 'wrap' | 'inline'> & {
  as?: ElementType;
  className?: string;
  children?: ReactNode;
  gap?: NumberVariant<GapValue>;
  wrap?: BooleanVariant;
  inline?: BooleanVariant;
};

const StackComponent = forwardRef<HTMLDivElement, StackProps>(
  (
    { children, as: Tag = 'div', className = '', direction = 'column', gap = 0, wrap, alignX, alignY, inline, ...rest },
    ref,
  ) => {
    const classNames = getClassNames(
      sparkyClassName,
      stackRecipe({
        direction,
        gap: gap as StackRecipeVariants['gap'],
        wrap: wrap as StackRecipeVariants['wrap'],
        alignX,
        alignY,
        inline: inline as StackRecipeVariants['inline'],
      }),
      className,
    );

    // Only add a div wrapper when we nest a Stack or give a text node as a child, to apply the correct margins
    const augmentedChildren = Children.toArray(children).map((child, index) => {
      if (typeof child === 'string') {
        if (child.trim() === '') return null;
        return <div key={`text-${index}`}>{child}</div>;
      }

      if (typeof child === 'number') {
        // A layout component wouldn't expect to have number 0 as direct child.
        // This is needed to prevent rendering a div having the value 0 when consumer code conditionally renders like {items.length && <Component/>}.
        // Since that usage is highly common and the implications would be negligible, we omit processing direct child 0.
        if (child === 0) return null;
        return <div key={`num-${index}`}>{child}</div>;
      }

      if (isValidElement(child)) {
        const isNestedStack = child.type.toString() === `.${sparkyClassName}`;
        return isNestedStack ? <div key={child.key ?? index}>{child}</div> : child;
      }

      return null;
    });

    return (
      <Tag ref={ref} className={classNames} {...rest}>
        {augmentedChildren}
      </Tag>
    );
  },
);

StackComponent.toString = () => `.${sparkyClassName}`;

// Compound components with forwardRef cause issues: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757#issuecomment-488848720
export const Stack = Object.assign({}, StackComponent, { Item: StackItem });

Stack.Item.displayName = 'Stack.Item';
StackComponent.displayName = 'Stack';
